import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts"
import { formatCurrency, formatNumber, formatCantidad, colorPara } from "../helpers"

const TooltipContent = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null
  const d = payload[0].payload
  return (
    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12, maxWidth: 320 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.descripcion}</div>
      <div>{formatCurrency(d.total_ventas)}</div>
      <div style={{ color: "#94a3b8", fontSize: 11 }}>
        {formatCantidad(d.unidades_vendidas)} uds · {formatCurrency(d.precio_promedio)} prom.
      </div>
    </div>
  )
}

TooltipContent.propTypes = { active: PropTypes.bool, payload: PropTypes.array }

const ProductosTab = ({ data }) => {
  const prods = data?.productos
  const [selected, setSelected] = useState("__global__")
  const [vista, setVista] = useState("ventas") // "ventas" | "unidades"

  const productos = useMemo(() => {
    if (!prods) return []
    let lista = []
    if (selected === "__global__") {
      lista = prods.global?.productos || []
    } else {
      const suc = (prods.sucursales || []).find(s => s.sucursal === selected)
      lista = suc?.productos || []
    }
    const sortBy = vista === "ventas" ? "total_ventas" : "unidades_vendidas"
    return [...lista].sort((a, b) => (Number(b[sortBy]) || 0) - (Number(a[sortBy]) || 0))
  }, [prods, selected, vista])

  if (!prods) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-package-variant" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin información de productos.</div>
      </div>
    )
  }

  const datos = productos.slice(0, 15).map((p, i) => ({
    ...p,
    descripcionCorta: (p.descripcion || "").length > 40 ? `${p.descripcion.slice(0, 39)}…` : p.descripcion || "(sin nombre)",
    color: colorPara(i),
  }))

  const dataKey = vista === "ventas" ? "total_ventas" : "unidades_vendidas"

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: 20 }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-package-variant-closed me-2 text-primary" />
            Top {Math.min(productos.length, 15)} productos
          </h6>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {prods.top_n ? `Backend devuelve top ${prods.top_n}` : null}
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ minWidth: 200 }}
            value={selected}
            onChange={e => setSelected(e.target.value)}
          >
            <option value="__global__">Global (todas las sucursales)</option>
            {(prods.sucursales || []).map(s => (
              <option key={s.sucursal} value={s.sucursal}>{s.sucursal}</option>
            ))}
          </select>
          <div style={{ display: "inline-flex", borderRadius: 6, background: "#f1f5f9", padding: 2 }}>
            {[
              { v: "ventas", l: "Ventas $" },
              { v: "unidades", l: "Unidades" },
            ].map(o => (
              <button
                key={o.v}
                type="button"
                onClick={() => setVista(o.v)}
                style={{
                  padding: "4px 12px",
                  border: "none",
                  background: vista === o.v ? "#fff" : "transparent",
                  color: vista === o.v ? "#0f172a" : "#64748b",
                  fontWeight: 600,
                  fontSize: 12,
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {datos.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}>
          Sin productos para esta selección.
        </div>
      ) : (
        <>
          <div style={{ width: "100%", height: Math.max(300, datos.length * 30) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={datos} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  tickFormatter={v => vista === "ventas" ? formatCurrency(v) : formatNumber(v)}
                />
                <YAxis
                  type="category"
                  dataKey="descripcionCorta"
                  width={250}
                  tick={{ fontSize: 11, fill: "#475569" }}
                  interval={0}
                />
                <Tooltip content={<TooltipContent />} cursor={{ fill: "rgba(59,130,246,0.08)" }} />
                <Bar dataKey={dataKey} radius={[0, 4, 4, 0]}>
                  {datos.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tabla completa */}
          <div style={{ marginTop: 20, maxHeight: 400, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1 }}>
                <tr>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>#</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Producto</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Unidades</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Total ventas</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, borderBottom: "1px solid #e2e8f0" }}>Precio prom.</th>
                </tr>
              </thead>
              <tbody>
                {productos.map((p, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "8px 12px", color: "#94a3b8", fontSize: 12 }}>{i + 1}</td>
                    <td style={{ padding: "8px 12px", color: "#0f172a", fontWeight: 500 }}>{p.descripcion}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>{formatCantidad(p.unidades_vendidas)}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#10b981", fontWeight: 600 }}>{formatCurrency(p.total_ventas)}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569" }}>{formatCurrency(p.precio_promedio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

ProductosTab.propTypes = { data: PropTypes.object }

export default ProductosTab
